import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { org } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Copy, CalendarDays, List, LayoutGrid, Eye, Upload, Image, ChevronUp, ChevronDown, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GAME_NAMES = {
  magic: 'Magic', pokemon: 'Pokémon', yugioh: 'Yu-Gi-Oh!',
  lorcana: 'Lorcana', onepiece: 'One Piece', dbs: 'DBS',
};

const DEFAULT_LAYOUTS = {
  planin: { enabled: true, primary_color: '#5C4EBF', bg_color: '#ffffff', text_color: '#333333', logo_url: '' },
  week: { enabled: true, primary_color: '#5C4EBF', bg_color: '#ffffff', text_color: '#333333', logo_url: '' },
  calendar: { enabled: true, primary_color: '#FF6B35', bg_color: '#1a1a1a', text_color: '#ffffff', logo_url: '' },
  agenda: { enabled: true, primary_color: '#4ECDC4', bg_color: '#0f1420', text_color: '#ffffff', logo_url: '' },
  list: { enabled: true, primary_color: '#FF6B35', bg_color: '#1a1a1a', text_color: '#ffffff', logo_url: '' },
  cards: { enabled: true, primary_color: '#FF6B35', bg_color: '#1a1a1a', text_color: '#ffffff', logo_url: '', cards_per_row: 3 },
};

function ColorPicker({ label, value, onChange }) {
  // Ensure hex value always has #
  const handleTextChange = (e) => {
    let v = e.target.value.trim();
    if (v && !v.startsWith('#')) v = '#' + v;
    onChange(v);
  };

  return (
    <div className="space-y-1">
      <Label className="text-[#B0B0B0] text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-[#333] bg-transparent" />
        <Input value={value || ''} onChange={handleTextChange} placeholder="#ffffff" className="bg-[#252525] border-[#333] text-white w-28 font-mono text-sm" />
      </div>
    </div>
  );
}

function GameLogosConfig({ gameLogos, onUpdate }) {
  const handleFileUpload = async (game, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('game', game);
    try {
      const res = await org.uploadGameLogo(formData);
      onUpdate({ ...gameLogos, [game]: res.data.url });
      toast.success(`Logo ${GAME_NAMES[game]} uploadé`);
    } catch {
      // Fallback: use data URL
      const reader = new FileReader();
      reader.onload = (ev) => {
        onUpdate({ ...gameLogos, [game]: ev.target.result });
        toast.success(`Logo ${GAME_NAMES[game]} ajouté`);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-[#B0B0B0] text-sm font-medium">Logos des jeux (optionnel)</Label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(GAME_NAMES).map(([key, name]) => (
          <div key={key} className="flex items-center gap-3 p-2 rounded-lg bg-[#252525]/50">
            <div className="w-8 h-8 rounded-lg bg-[#333] flex items-center justify-center overflow-hidden flex-shrink-0">
              {gameLogos?.[key] ? (
                <img src={gameLogos[key]} alt={name} className="w-full h-full object-contain" />
              ) : (
                <Image className="w-4 h-4 text-[#666]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-[#B0B0B0] block truncate">{name}</span>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(key, e)} />
              <Upload className="w-3.5 h-3.5 text-[#666] hover:text-[#FF6B35] transition-colors" />
            </label>
            {gameLogos?.[key] && (
              <button onClick={() => onUpdate({ ...gameLogos, [key]: '' })} className="text-[#666] hover:text-red-400 text-xs">
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LayoutConfig({ layoutKey, config, onChange, orgId, gameLogos, onGameLogosChange }) {
  const [codeExpanded, setCodeExpanded] = useState(false);
  
  // Short embed URL (config fetched from API) — used for the copyable iframe code
  const shortUrl = `${window.location.origin}/embed/${orgId}?l=${layoutKey}`;
  const shortIframeCode = `<iframe src="${shortUrl}" width="100%" height="600" frameborder="0" style="border-radius:8px"></iframe>`;

  // Live preview URL — inline params reflect the CURRENT (unsaved) config in real time
  const previewUrl = (() => {
    const p = new URLSearchParams();
    p.set('layout', layoutKey);
    if (config.primary_color) p.set('primary', config.primary_color);
    if (config.bg_color) p.set('bg', config.bg_color);
    if (config.text_color) p.set('text', config.text_color);
    p.set('sep', config.separator_color || 'game');
    p.set('gd', config.game_display || 'name');
    if (layoutKey === 'cards') p.set('cols', String(config.cards_per_row || 3));
    if (config.logo_url) p.set('logo', config.logo_url);
    if (config.game_display === 'logo' && gameLogos) {
      Object.entries(gameLogos).forEach(([g, url]) => { if (url) p.set(`gl_${g}`, url); });
    }
    return `${window.location.origin}/embed/${orgId}?${p.toString()}`;
  })();

  const copyCode = () => {
    navigator.clipboard.writeText(shortIframeCode);
    toast.success('Code iframe copié !');
  };

  // Debounce the preview URL so dragging the colour picker doesn't reload the iframe on every tick
  const [debouncedPreviewUrl, setDebouncedPreviewUrl] = useState(previewUrl);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedPreviewUrl(previewUrl), 350);
    return () => clearTimeout(id);
  }, [previewUrl]);

  return (
    <div className="space-y-4">
      {/* Colors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ColorPicker label="Couleur principale" value={config.primary_color} onChange={(v) => onChange({ ...config, primary_color: v })} />
        <ColorPicker label="Couleur de fond" value={config.bg_color} onChange={(v) => onChange({ ...config, bg_color: v })} />
        <ColorPicker label="Couleur du texte" value={config.text_color} onChange={(v) => onChange({ ...config, text_color: v })} />
      </div>

      {/* Separator color */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#B0B0B0] text-sm">Couleur du séparateur</Label>
          <Select value={config.separator_color || 'game'} onValueChange={(v) => onChange({ ...config, separator_color: v })}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="game" className="text-white">Couleur du jeu (variable)</SelectItem>
              <SelectItem value="custom" className="text-white">Couleur personnalisée (uniforme)</SelectItem>
            </SelectContent>
          </Select>
          {config.separator_color === 'custom' && (
            <div className="mt-2">
              <ColorPicker label="" value={config.separator_custom_color || config.primary_color} onChange={(v) => onChange({ ...config, separator_color: v })} />
            </div>
          )}
        </div>
        <div>
          <Label className="text-[#B0B0B0] text-sm">Affichage du jeu</Label>
          <Select value={config.game_display || 'name'} onValueChange={(v) => onChange({ ...config, game_display: v })}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="name" className="text-white">Nom du jeu</SelectItem>
              <SelectItem value="logo" className="text-white">Logo du jeu</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Game logos (only when logo display mode) */}
      {config.game_display === 'logo' && (
        <GameLogosConfig gameLogos={gameLogos} onUpdate={onGameLogosChange} />
      )}

      {/* Logo URL */}
      <div>
        <Label className="text-[#B0B0B0] text-sm">URL du logo (optionnel)</Label>
        <Input value={config.logo_url || ''} onChange={(e) => onChange({ ...config, logo_url: e.target.value })} placeholder="https://mon-site.com/logo.png" className="bg-[#252525] border-[#333] text-white" />
      </div>

      {/* Cards per row */}
      {layoutKey === 'cards' && (
        <div>
          <Label className="text-[#B0B0B0] text-sm">Cartes par ligne: {config.cards_per_row || 3}</Label>
          <Slider value={[config.cards_per_row || 3]} onValueChange={([v]) => onChange({ ...config, cards_per_row: v })} min={1} max={6} step={1} className="mt-2" />
        </div>
      )}

      {/* Font & Size */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-[#B0B0B0] text-sm">Police</Label>
          <Select value={config.font_family || 'system'} onValueChange={(v) => onChange({ ...config, font_family: v })}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="system" className="text-white">Système (défaut)</SelectItem>
              <SelectItem value="Inter" className="text-white">Inter</SelectItem>
              <SelectItem value="Roboto" className="text-white">Roboto</SelectItem>
              <SelectItem value="Poppins" className="text-white">Poppins</SelectItem>
              <SelectItem value="Open Sans" className="text-white">Open Sans</SelectItem>
              <SelectItem value="Montserrat" className="text-white">Montserrat</SelectItem>
              <SelectItem value="Lato" className="text-white">Lato</SelectItem>
              <SelectItem value="Nunito" className="text-white">Nunito</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#B0B0B0] text-sm">Taille police</Label>
          <Select value={String(config.font_size || 14)} onValueChange={(v) => onChange({ ...config, font_size: parseInt(v) })}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="11" className="text-white">Petit (11px)</SelectItem>
              <SelectItem value="13" className="text-white">Normal (13px)</SelectItem>
              <SelectItem value="14" className="text-white">Moyen (14px)</SelectItem>
              <SelectItem value="16" className="text-white">Grand (16px)</SelectItem>
              <SelectItem value="18" className="text-white">Très grand (18px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#B0B0B0] text-sm">Hauteur iframe</Label>
          <Select value={String(config.iframe_height || 600)} onValueChange={(v) => onChange({ ...config, iframe_height: parseInt(v) })}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="400" className="text-white">Petit (400px)</SelectItem>
              <SelectItem value="500" className="text-white">Moyen (500px)</SelectItem>
              <SelectItem value="600" className="text-white">Normal (600px)</SelectItem>
              <SelectItem value="800" className="text-white">Grand (800px)</SelectItem>
              <SelectItem value="0" className="text-white">Auto (100%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Iframe code */}
      <div className="p-4 rounded-lg bg-[#252525] border border-[#333]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Label className="text-[#B0B0B0] text-sm font-medium">Code iframe</Label>
            <span className="text-[10px] text-[#555] bg-[#333] px-2 py-0.5 rounded-full">{shortIframeCode.length} car.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCodeExpanded(!codeExpanded)} className="text-[#666] hover:text-white gap-1">
              {codeExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {codeExpanded ? 'Réduire' : 'Voir'}
            </Button>
            <Button variant="ghost" size="sm" onClick={copyCode} className="text-[#FF6B35] hover:text-[#E55A25] gap-1" data-testid={`copy-iframe-${layoutKey}`}><Copy className="w-3 h-3" />Copier</Button>
          </div>
        </div>
        {codeExpanded && (
          <pre className="text-xs text-[#888] overflow-x-auto whitespace-pre-wrap break-all font-mono mt-2 p-2 bg-[#1a1a1a] rounded border border-[#333]">{shortIframeCode}</pre>
        )}
      </div>

      {/* Preview */}
      <div>
        <div className="flex items-center gap-2 mb-2"><Eye className="w-4 h-4 text-[#666]" /><Label className="text-[#B0B0B0] text-sm font-medium">Aperçu en temps réel</Label></div>
        <div className="rounded-lg overflow-hidden border border-[#333]" style={{ height: 400 }}>
          <iframe key={debouncedPreviewUrl} src={debouncedPreviewUrl} width="100%" height="100%" frameBorder="0" title={`Preview ${layoutKey}`} />
        </div>
      </div>
    </div>
  );
}

export default function IframeManagerPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [gameLogos, setGameLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    org.getIframeConfig().then(r => {
      setConfig(r.data);
      setGameLogos(r.data?.game_logos || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await org.updateIframeConfig({ layouts: config.layouts, game_logos: gameLogos });
      toast.success('Configuration sauvegardée');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateLayout = (key, value) => {
    setConfig(prev => ({ ...prev, layouts: { ...prev.layouts, [key]: value } }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white" data-testid="iframe-title">Widgets Iframe</h1><p className="text-[#666] mt-1">Intégrez votre planning de tournois sur votre site web</p></div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B35] hover:bg-[#E55A25]" data-testid="save-iframe-config">{saving ? 'Sauvegarde...' : 'Sauvegarder'}</Button>
      </div>

      <Tabs defaultValue="calendar">
        <TabsList className="bg-[#1a1a1a] border border-[#333]">
          <TabsTrigger value="planin" className="gap-2 data-[state=active]:bg-[#5C4EBF] data-[state=active]:text-white"><List className="w-4 h-4" />Planin</TabsTrigger>
          <TabsTrigger value="week" className="gap-2 data-[state=active]:bg-[#5C4EBF] data-[state=active]:text-white"><CalendarRange className="w-4 h-4" />Semaine</TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2 data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white"><CalendarDays className="w-4 h-4" />Calendrier</TabsTrigger>
          <TabsTrigger value="agenda" className="gap-2 data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-[#0f1420]"><CalendarDays className="w-4 h-4" />Agenda</TabsTrigger>
          <TabsTrigger value="list" className="gap-2 data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white"><List className="w-4 h-4" />Liste</TabsTrigger>
          <TabsTrigger value="cards" className="gap-2 data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white"><LayoutGrid className="w-4 h-4" />Cartes</TabsTrigger>
        </TabsList>

        {['planin', 'week', 'calendar', 'agenda', 'list', 'cards'].map(key => (
          <TabsContent key={key} value={key}>
            <Card className="bg-[#1a1a1a] border-[#333]">
              <CardHeader><CardTitle className="text-white flex items-center gap-2">
                {key === 'planin' && <><List className="w-5 h-5 text-[#5C4EBF]" />Layout Planin</>}
                {key === 'week' && <><CalendarRange className="w-5 h-5 text-[#5C4EBF]" />Layout Semaine</>}
                {key === 'calendar' && <><CalendarDays className="w-5 h-5 text-[#FF6B35]" />Layout Calendrier</>}
                {key === 'agenda' && <><CalendarDays className="w-5 h-5 text-[#4ECDC4]" />Layout Agenda (calendrier mensuel)</>}
                {key === 'list' && <><List className="w-5 h-5 text-[#4ECDC4]" />Layout Liste</>}
                {key === 'cards' && <><LayoutGrid className="w-5 h-5 text-[#7B68EE]" />Layout Cartes</>}
              </CardTitle></CardHeader>
              <CardContent>
                <LayoutConfig
                  layoutKey={key}
                  config={config?.layouts?.[key] || DEFAULT_LAYOUTS[key]}
                  onChange={(v) => updateLayout(key, v)}
                  orgId={user?.id}
                  gameLogos={gameLogos}
                  onGameLogosChange={setGameLogos}
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
